import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Space, Typography, Avatar } from "antd";
import {
  UserOutlined,
  ShoppingOutlined,
  FireOutlined,
  StarFilled,
  ThunderboltOutlined,
  EyeOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../../config/api";
import "./SocialProofNotifications.css";

const { Text, Paragraph } = Typography;

/**
 * Interface pour un événement de preuve sociale
 */
interface SocialProofEvent {
  id: number;
  event_type:
    | "purchase"
    | "view"
    | "low_stock"
    | "best_seller"
    | "trending"
    | "new_review";
  event_data: {
    message?: string;
    product_name?: string;
    customer_city?: string;
    quantity?: number;
    rating?: number;
    stock_remaining?: number;
  };
  display_priority: number;
  created_at: string;
}

/**
 * Props du composant SocialProofNotifications
 */
interface SocialProofNotificationsProps {
  /** Slug de la boutique */
  storeSlug: string;

  /** Intervalle d'affichage des notifications (ms, défaut: 10000 = 10s) */
  displayInterval?: number;

  /** Durée d'affichage d'une notification (ms, défaut: 6000 = 6s) */
  displayDuration?: number;

  /** Intervalle de rafraîchissement des données (ms, défaut: 30000 = 30s) */
  refreshInterval?: number;

  /** Position des notifications ('bottom-left' | 'bottom-right' | 'top-left' | 'top-right') */
  position?: "bottom-left" | "bottom-right" | "top-left" | "top-right";

  /** Activer/désactiver les notifications (défaut: true) */
  enabled?: boolean;
}

/**
 * Composant SocialProofNotifications
 *
 * Affiche des notifications popups dynamiques pour créer de la preuve sociale
 * (achats récents, produits populaires, stock faible, nouveaux avis, etc.)
 *
 * @example
 * <SocialProofNotifications
 *   storeSlug="ma-boutique"
 *   position="bottom-left"
 *   displayInterval={12000}
 *   enabled={true}
 * />
 */
const SocialProofNotifications: React.FC<SocialProofNotificationsProps> = ({
  storeSlug,
  displayInterval = 10000,
  displayDuration = 6000,
  refreshInterval = 30000,
  position = "bottom-left",
  enabled = true,
}) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768;
    }
    return false;
  });
  const [events, setEvents] = useState<SocialProofEvent[]>([]);
  const [currentEvent, setCurrentEvent] = useState<SocialProofEvent | null>(
    null
  );
  const [visible, setVisible] = useState(false);
  const currentIndexRef = useRef(0);
  const hideTimeoutRef = useRef<number | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  /**
   * Récupère les événements de preuves sociales depuis l'API
   */
  const fetchSocialProofEvents = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await fetch(
        `${API_CONFIG.PUBLIC_STORE_API}/${storeSlug}/social-proof?limit=10`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          skipGlobalLoading: true,
        }
      );

      const data = await response.json();

      if (data.success && data.events && data.events.length > 0) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error("Erreur fetch preuves sociales:", error);
    }
  }, [storeSlug, enabled]);

  /**
   * Charge les événements au montage et à intervalles réguliers
   */
  useEffect(() => {
    fetchSocialProofEvents();

    const interval = setInterval(fetchSocialProofEvents, refreshInterval);

    return () => clearInterval(interval);
  }, [fetchSocialProofEvents, refreshInterval]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /**
   * Gère l'affichage rotatif des notifications
   */
  useEffect(() => {
    if (!enabled || events.length === 0) {
      clearHideTimeout();
      setVisible(false);
      return;
    }

    const showNotification = () => {
      const safeIndex = currentIndexRef.current % events.length;
      const event = events[safeIndex];
      setCurrentEvent(event);
      setVisible(true);

      // Masquer après displayDuration
      clearHideTimeout();
      hideTimeoutRef.current = window.setTimeout(() => {
        setVisible(false);
      }, displayDuration);

      // Passer à l'événement suivant
      currentIndexRef.current = (safeIndex + 1) % events.length;
    };

    // Afficher immédiatement la première fois
    showNotification();

    // Puis afficher à intervalles réguliers
    const interval = setInterval(showNotification, displayInterval);

    return () => {
      clearInterval(interval);
      clearHideTimeout();
    };
  }, [
    events,
    displayInterval,
    displayDuration,
    enabled,
    clearHideTimeout,
  ]);

  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  /**
   * Ferme manuellement la notification
   */
  const handleClose = () => {
    setVisible(false);
  };

  /**
   * Retourne l'icône selon le type d'événement
   */
  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case "purchase":
        return <ShoppingOutlined style={{ fontSize: 20, color: "#52c41a" }} />;
      case "new_review":
        return <StarFilled style={{ fontSize: 20, color: "#faad14" }} />;
      case "low_stock":
        return (
          <ThunderboltOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />
        );
      case "best_seller":
        return <FireOutlined style={{ fontSize: 20, color: "#ff7a45" }} />;
      case "trending":
        return <FireOutlined style={{ fontSize: 20, color: "#1890ff" }} />;
      case "view":
        return <EyeOutlined style={{ fontSize: 20, color: "#722ed1" }} />;
      default:
        return <UserOutlined style={{ fontSize: 20, color: "#1890ff" }} />;
    }
  };

  /**
   * Retourne le message formaté selon le type d'événement
   */
  const getEventMessage = (event: SocialProofEvent): string => {
    const { event_type, event_data } = event;

    if (event_data.message) {
      return event_data.message;
    }

    switch (event_type) {
      case "purchase":
        return `${event_data.customer_city || "Un client"} a acheté ${
          event_data.product_name || "ce produit"
        }`;

      case "new_review":
        const stars = "⭐".repeat(event_data.rating || 5);
        return `Nouveau : ${stars} sur ${event_data.product_name || "ce produit"}`;

      case "low_stock":
        return `Plus que ${event_data.stock_remaining || 3} en stock !`;

      case "best_seller":
        return `🔥 Meilleure vente : ${
          event_data.quantity || 0
        } vendus cette semaine`;

      case "trending":
        return `📈 ${event_data.product_name || "Produit"} tendance`;

      case "view":
        return `👀 ${event_data.quantity || 0} personnes regardent ce produit`;

      default:
        return "Activité récente sur la boutique";
    }
  };

  /**
   * Retourne le temps relatif (ex: "Il y a 2h")
   */
  const getRelativeTime = (createdAt: string): string => {
    const now = new Date();
    const eventDate = new Date(createdAt);
    const diffMs = now.getTime() - eventDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return "à l'instant";
    if (diffMinutes < 60) return `il y a ${diffMinutes} min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays === 1) return "hier";
    if (diffDays < 7) return `il y a ${diffDays}j`;
    return "récemment";
  };

  /**
   * Calcule les styles de position
   */
  const getPositionStyles = () => {
    const base = {
      position: "fixed" as const,
      zIndex: 9999,
      animation: visible ? "slideIn 0.4s ease-out" : "slideOut 0.4s ease-out",
    };

    switch (position) {
      case "bottom-left":
        return isMobile
          ? { ...base, bottom: 12, left: 12, right: 12 }
          : { ...base, bottom: 20, left: 20 };
      case "bottom-right":
        return isMobile
          ? { ...base, bottom: 12, left: 12, right: 12 }
          : { ...base, bottom: 20, right: 20 };
      case "top-left":
        return isMobile
          ? { ...base, top: 72, left: 12, right: 12 }
          : { ...base, top: 80, left: 20 };
      case "top-right":
        return isMobile
          ? { ...base, top: 72, left: 12, right: 12 }
          : { ...base, top: 80, right: 20 };
      default:
        return isMobile
          ? { ...base, bottom: 12, left: 12, right: 12 }
          : { ...base, bottom: 20, left: 20 };
    }
  };

  if (!enabled || !visible || !currentEvent) {
    return null;
  }

  return (
    <div style={getPositionStyles()} className="social-proof-notification">
      <Card
        size="small"
        style={{
          width: isMobile ? "100%" : 320,
          maxWidth: isMobile ? "calc(100vw - 24px)" : 320,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          borderRadius: 8,
          border: "1px solid #f0f0f0",
          cursor: "pointer",
        }}
        bodyStyle={{ padding: 12 }}
      >
        <Space size={12} align="start" style={{ width: "100%" }}>
          {/* Icône */}
          <Avatar
            size={40}
            icon={getEventIcon(currentEvent.event_type)}
            style={{
              backgroundColor: "#f0f0f0",
              flexShrink: 0,
            }}
          />

          {/* Contenu */}
          <Space
            direction="vertical"
            size={2}
            style={{ flex: 1, minWidth: 0 }}
          >
            <Paragraph
              strong
              style={{
                fontSize: 13,
                lineHeight: 1.4,
                marginBottom: 0,
              }}
              ellipsis={{ rows: 2 }}
            >
              {getEventMessage(currentEvent)}
            </Paragraph>
            <Text
              type="secondary"
              style={{
                fontSize: 11,
                display: "block",
              }}
            >
              {getRelativeTime(currentEvent.created_at)}
            </Text>
          </Space>

          {/* Bouton fermer */}
          <CloseOutlined
            onClick={handleClose}
            style={{
              fontSize: 12,
              color: "#8c8c8c",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default SocialProofNotifications;
